import React, { useState, useMemo } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';
import MdbMissingAccountCardsTable from './MdbMissingAccountCardsTable';
import dayjs from 'dayjs';

const TaiodetJournalImportModal = ({
    open,
    onClose,
    previewTaiodetJournal,
    commitTaiodetJournal,
    direction,
    t,
    client,
    token,
    palCodes = [],
    accountCardOptions = [],
    fetchEnums,
    setLoading,
    user,
    setAccountCardOptions,
    CustomMonthDatePicker
}) => {
    const [cardReportFile, setCardReportFile] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [date, setDate] = useState(dayjs());

    const [missingAccountCards, setMissingAccountCards] = useState([]);
    const [parsedRows, setParsedRows] = useState(0);

    const handleCardReportFileDrop = files => {
        if (files?.length) setCardReportFile(files[0]);
    };

    const handlePreview = async () => {
        if (!cardReportFile) return;

        try {
            setProcessing(true);
            setPreviewDone(false);

            const res = await previewTaiodetJournal(cardReportFile, date);
            
            const missingCards = res?.missingAccountCards ?? [];
            const rows = res?.parsedRows ?? 0;

            setMissingAccountCards(missingCards);
            setParsedRows(rows);

            // Fetch account cards when preview is done (for create card functionality)
            if (client && token && fetchEnums) {
                try {
                    await fetchEnums(user, token, client);
                } catch (error) {
                    console.error('Error fetching account cards:', error);
                }
            }

            setPreviewDone(true);
        } catch (err) {
            console.error('Taiodet journal preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateAndCommit = async () => {
        if (!cardReportFile) return;

        try {
            setProcessing(true);

            // Import journal entries
            const res = await commitTaiodetJournal(cardReportFile, date);

            const errorCount = res?.errorCount ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Import errors:', errorMessages);
                // Don't close modal - let user see the error
            } else {
                // Success - close modal
                handleClose();
            }
        } catch (err) {
            console.error('Taiodet journal commit error:', err);
            // Don't close modal on error
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setCardReportFile(null);
        setPreviewDone(false);
        setMissingAccountCards([]);
        setParsedRows(0);
        setDate(dayjs());
        onClose();
    };

    if (!open) return null;

    return (
        <>
            <style>
                {`
                    .MuiPickersPopper-root {
                        z-index: 3000 !important;
                    }
                    .MuiPopper-root {
                        z-index: 3000 !important;
                    }
                `}
            </style>
            <div className="download-modal-overlay" onClick={handleClose} style={{ zIndex: 2000 }}>
                <div className="download-modal" onClick={e => e.stopPropagation()} style={{ zIndex: 2001 }}>
                    <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            {t('ImportTaiodetJournalEntries')}
                        </h4>
                        <IconButton
                            className="client-back-icon"
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px', textAlign: direction === 'rtl' ? 'right' : 'left' }}>
                        {t('TaiodetJournalFormat')}
                    </p>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {/* Date Picker */}
                        <div style={{ position: 'relative', zIndex: 3000 }}>
                            <h5>{t('DocumentDate')}</h5>
                            <CustomMonthDatePicker
                                onDateChange={(date) => setDate(date)}
                                date={date}
                                label={t('SelectDate')}
                                height="55px"
                            />
                        </div>

                        {/* Card Report File Upload */}
                        <div>
                            <h5>{t('CardReportFile')} (.csv)</h5>
                            <DropzoneArea
                                key={cardReportFile ? cardReportFile.name : 'taiodet-journal-empty'}
                                onDrop={handleCardReportFileDrop}
                                accept=".csv"
                                filesLimit={1}
                            />
                            {cardReportFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {cardReportFile.name}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handlePreview}
                            disabled={!cardReportFile || processing || !date}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing || missingAccountCards.length > 0}
                            onClick={handleCreateAndCommit}
                        >
                            {processing ? t('Processing') : t('CreateAndImport')}
                        </Button>
                    </div>

                    {previewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15 }}>
                                <p style={{ fontSize: '14px', color: '#666' }}>
                                    {t('ParsedRows')}: <strong>{parsedRows}</strong>
                                </p>
                            </div>

                            {/* Missing Account Cards Table */}
                            {missingAccountCards.length > 0 && (
                                <div style={{ marginTop: 25 }}>
                                    <MdbMissingAccountCardsTable 
                                        missingAccountCards={missingAccountCards.map(card => ({
                                            cardId: card.cardId || card,
                                            cardName: card.cardName || ''
                                        }))}
                                        direction={direction}
                                        client={client}
                                        token={token}
                                        palCodes={palCodes || []}
                                        accountCardOptions={accountCardOptions}
                                        fetchEnums={fetchEnums}
                                        setLoading={setLoading}
                                        isRange={false}
                                        isClosedReportingMonth={false}
                                        sortCodes={[]}
                                        user={user}
                                        setAccountCardOptions={setAccountCardOptions}
                                        onCardCreated={async () => {
                                            // Refresh preview after card is created
                                            if (cardReportFile) {
                                                await handlePreview();
                                            }
                                        }}
                                    />
                                </div>
                            )}

                            {missingAccountCards.length === 0 && (
                                <p style={{ color: 'green' }}>{t('NoMissingAccountCards')}</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
        </>
    );
};

export default TaiodetJournalImportModal;

